import { Popover, Tooltip } from 'bootstrap';
import userPreferencesService from '@common/Services/preferences-service';
import { getPageHelpTexts, savePageHelpText } from '@common/Services/client-data-service';

const editModeIsOn = () => {
    return !!new URLSearchParams(window.location.search).get('EditMode');
};
const editModeToggle = () => $('.ccib-edit-mode-toggle');
const pageId = () => $('#PageId').val()?.toString();
const pageButtons = () => $('#main button, #main input[type="submit"]').not('.accordion-button').not('.nav-link');
const pageLinkButtons = () => $('#main a.btn, #main a[role="button"]').not('.nav-link');
const pageInputs = () => $('#main input:not(.js-rater-box *), #main select, #main input[type="checkbox"]');
const manualElements = () => $('#main .ccib-manual-help-element');
const manualLabels = () => $('#main .ccib-manual-help-label');
const editIdPrefix = 'HelpTextFor_';
const editButtonIdPrefix = 'HelpTextSaveFor_';
let helpTextManager: HelpTextManager;

export async function InitializeHelpText() {
    if (!!pageId()) {
        helpTextManager = new HelpTextManager(pageId());
        await helpTextManager.InitializeData();

        InitializeMenu();

        await InitializeHelpTextLabels();
    }
}

function InitializeMenu() {
    const params = new URLSearchParams(window.location.search);
    editModeToggle().on('click', () => {
        if (editModeIsOn()) {
            params.delete('EditMode');
        } else {
            params.set('EditMode', 'true');
        }
        history.pushState(null, null, "?" + params.toString());
        window.location.reload();
    });
}

async function InitializeHelpTextLabels() {
    if (editModeIsOn()) {
        pageButtons().attr('disabled', 'disabled');
        pageLinkButtons().addClass('disabled');
        manualLabels().removeAttr('hidden');

        applyInputPopovers();
    } else {
        userPreferencesService.preferences.then((preferences) => {
            if (preferences.showInputHelp) {
                applyHelpTextTooltips();
            }
        });
    }
}

function applyInputPopovers() {
    pageInputs().each((_, element) => {
        // TODO: provide a solution to checkbox and toggle labels so that OCC Users can
        //       edit them without triggering the toggle or checkbox
        if ($(element).parent().hasClass('form-toggle') || $(element).parent().hasClass('form-check')) {
            // skip toggle and checkbox inputs for now ¯\_(ツ)_/¯
            // return;
        }

        // does the element have an input-id attribute?
        //   if so, decode it (see ValidationTagHelper.cs)
        //   if not, grab the normal id attribute
        const fieldId = $(element).attr('input-id')
            ? atob($(element).attr('input-id'))
            : element.id;    
        const label = $(`[for="${element.id}"]`);

        if (label.length > 0) {
            label.addClass('ccib-help-text-label');
            label.addClass(helpTextManager.GetHelpText(fieldId) ? 'ccib-help-text-label-exists' : 'ccib-help-text-label-new');

            setupLabelPopover(label, fieldId);
        }
    });

    manualElements().each((_, element) => {
        const elementId = element.id;
        const label = $(`#${elementId}_label`);

        label.addClass(helpTextManager.GetHelpText(elementId) ? 'ccib-help-text-label-exists' : 'ccib-help-text-label-new');

        setupLabelPopover(label, elementId);
    })
}

function applyHelpTextTooltips() {
    const helpTexts = helpTextManager.GetHelpTexts();

    helpTexts.forEach((textEntry) => {
        if (!!textEntry.value) {
            // see if there's an input element on this page for the help text entry
            let input: HTMLElement;
            const inputSelector = $(`#${textEntry.key}`);
            if (inputSelector.length > 0) {
                // element matched on normal id attribute
                input = inputSelector[0];
            } else {
                // attempt to match element on encoded input-id attribute (see ValidationTagHelper.cs)
                const encodedId = btoa(textEntry.key);
                const inputSelector = $(`[input-id="${encodedId}"]`);
                input = inputSelector.length > 0 ? inputSelector[0] : null;
            }

            // if an input element exists...
            if (input !== null) {
                const label = $(`[for="${input.id}"]`);

                // if there's a label for this input element...
                if (label.length > 0) {
                    label.addClass('ccib-help-text-label');
                    setupLabelTooltip(label, textEntry.value);
                }
            }
        }
    });
}

function setupLabelPopover(label: JQuery<Element>, fieldId: string) {
    // setup popover element ids
    const safeFieldId = fieldId.replace(/\./g, '_');
    const fieldEditButtonId = `${editButtonIdPrefix}${safeFieldId}`;
    const fieldEditId = `${editIdPrefix}${safeFieldId}`;
    const title = label.text().length > 0 ? label.text() : label.attr('title');

    // setup the popover
    const popover = new Popover(label[0], {
        container: 'body',
        placement: 'top',
        trigger: 'manual',
        title: `${ title } Help Text <a id="${fieldEditButtonId}" role="button" class="float-end"><i class="bi bi-x-lg"></i></a>`,
        sanitize: false,
        html: true,
        content: () => { return `<textarea id="${fieldEditId}" class="form-control" rows="3" cols="80">${helpTextManager.GetHelpText(fieldId)}</textarea>`; }
    });

    // setup popover manual trigger open
    label.on('mouseover', () => {
        label.addClass('ccib-help-text-label-editing');
        popover.show();

        // setup popover manual trigger close
        $(`#${fieldEditButtonId}`).on('click', async (e) => {
            label.removeClass('ccib-help-text-label-editing');
            const newHelpText = $(`#${fieldEditId}`).val().toString();

            if (await helpTextManager.UpdateHelpText(fieldId, newHelpText)) {
                if (!!newHelpText) {
                    label.addClass('ccib-help-text-label-exists');
                    label.removeClass('ccib-help-text-label-new');
                } else {
                    label.addClass('ccib-help-text-label-new');
                    label.removeClass('ccib-help-text-label-exists');
                }
            }

            popover.hide();
            e.preventDefault();
        });
    });
}

function setupLabelTooltip(label: JQuery<Element>, helpText: string) {
    label.attr('data-bs-toggle', 'tooltip');
    if (!label.attr('data-bs-placement')) {
        label.attr('data-bs-placement', 'top');
    }
    label.attr('title', helpText);
    const _ = new Tooltip(label[0]);
}

interface KeyValuePair {
    key: string;
    value: string;
}

// class that facilitates interacting with the Help Text data
class HelpTextManager {
    private pageId: string;
    private helpTexts: any;

    constructor(pageId: string) {
        this.pageId = pageId;
    }

    async InitializeData() {
        this.helpTexts = await getPageHelpTexts(this.pageId);
    }

    GetHelpTexts() {
        var resultSet: KeyValuePair[] = [];
        for (var key in this.helpTexts) {
            if (this.helpTexts.hasOwnProperty(key)) {
                resultSet.push({ key, value: this.helpTexts[key] });
            }
        }
        return resultSet;
    }

    GetHelpText(fieldId: string) {
        return this.helpTexts[fieldId] || '';
    }

    async UpdateHelpText(fieldId: string, helpText: string) {
        const result = await savePageHelpText(this.pageId, fieldId, helpText);
        if (result) {
            this.helpTexts[fieldId] = helpText;
        }
        return result;
    }
}